import { useRequest } from 'ahooks';
import useNotification from 'antd/es/notification/useNotification';
import { useAuth } from 'auth/AuthContext';
import { AxiosError } from 'axios';
import RegisterLicenseCodeStep from 'components/forms/RegisterLicenseCodeStep';
import LoginRegisterForm from 'components/forms/LoginRegisterForm';
import posthog from 'posthog-js';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createSearchParams, useNavigate } from 'react-router-dom';
import { routes } from 'services/RouteService';
import ApiUsersManager, { ApiUsersSignUpParams } from 'services/api/ApiUsersManager';

export default function RegisterPage() {
  const { t } = useTranslation();
  const [api, context] = useNotification();
  const { status, login } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<1 | 2>(1);
  const [registerValues, setRegisterValues] = useState<ApiUsersSignUpParams | null>(null);
  const [displayCodeFull, setDisplayCodeFull] = useState(false);
  const [displayCodeExpired, setDisplayCodeExpired] = useState(false);
  const [passwordServerError, setPasswordServerError] = useState<string | undefined>(undefined);
  const [savedLicenseCode, setSavedLicenseCode] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (status === 'authenticated') {
      navigate({
        pathname: routes.onboarding.route,
        search: createSearchParams({
          questionnaire: 'true',
        }).toString(),
      });
    }
  }, [navigate, status]);

  const { loading, run } = useRequest(params => ApiUsersManager.signUp(params), {
    manual: true,
    onSuccess: async (_, params) => {
      try {
        await login({ email: params[0].email, password: params[0].password });
        posthog.capture('registration.completed');
      } catch {
        // login errors are handled on the login page paths; keep silent here
      }
    },
    onError: error => {
      if (error instanceof AxiosError && error.response?.status === 400) {
        const passwordErrors = error.response?.data?.password;
        if (Array.isArray(passwordErrors) && passwordErrors.length > 0) {
          setPasswordServerError(passwordErrors[0]);
          setStep(2);
          return;
        }
      }
      if (error instanceof AxiosError && error.response?.status === 403) {
        const errorCode = error.response?.data.code;
        if (errorCode === 'invalid_code') {
          api.error({
            message: t('loginRegister.errors.licenseCodeInvalid'),
          });
          setStep(1);
          return;
        }
        if (errorCode === 'contract_full') {
          setDisplayCodeFull(true);
          setStep(1);
          return;
        }
        if (errorCode === 'contract_expired') {
          setDisplayCodeExpired(true);
          setStep(1);
          return;
        }
      }
      api.error({
        message: t('loginRegister.errors.register'),
      });
    },
  });

  const onStep1Finish = (licenseCode?: string) => {
    setSavedLicenseCode(licenseCode);
    setDisplayCodeFull(false);
    setDisplayCodeExpired(false);
    setStep(2);
  };

  const onStep2Finish = (values: ApiUsersSignUpParams) => {
    setPasswordServerError(undefined);
    setRegisterValues(values);
    run({ ...values, license_code: savedLicenseCode });
  };

  return (
    <>
      {context}
      {step === 1 && (
        <RegisterLicenseCodeStep
          loading={loading || status === 'loading'}
          displayCodeFull={displayCodeFull}
          displayCodeExpired={displayCodeExpired}
          onSubmit={onStep1Finish}
          onBack={() => navigate(-1)}
          initialLicenseCode={savedLicenseCode}
        />
      )}
      {step === 2 && (
        <LoginRegisterForm
          title={t('loginRegister.title.register')}
          description=""
          buttonTitle={t('loginRegister.actions.next')}
          onButtonClick={params => onStep2Finish(params as ApiUsersSignUpParams)}
          buttonLoading={loading || status === 'loading'}
          mode="register"
          bottomLinkTo="login"
          onBackButtonClick={() => setStep(1)}
          passwordServerError={passwordServerError}
          initialValues={
            registerValues
              ? {
                  email: registerValues.email,
                  re_email: registerValues.email,
                  password: registerValues.password,
                  re_password: registerValues.re_password,
                  first_name: registerValues.first_name,
                  last_name: registerValues.last_name,
                  tc_and_privacy_policy_consent: registerValues.tc_and_privacy_policy_consent,
                  personal_information_access_consent: registerValues.personal_information_access_consent,
                }
              : undefined
          }
        />
      )}
    </>
  );
}
